import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'antd';
import Dropdown from 'components/Elements/Dropdown';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import './reopenIncidentModal.scss';

interface ReopenIncidentModalProps {
    visible: boolean;
    onClose: () => void;
    // Active statuses (excluding Closed) the incident can be reopened into.
    statuses: DropdownOption[];
    // Return false (or a Promise resolving to false) to keep the modal open on failure
    onConfirm: (incidentStatusId: string) => Promise<boolean | void> | boolean | void;
}

const ReopenIncidentModal: React.FC<ReopenIncidentModalProps> = ({
    visible,
    onClose,
    statuses,
    onConfirm,
}) => {
    const [statusId, setStatusId] = useState('');
    const [statusError, setStatusError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (visible) {
            setStatusId('');
            setStatusError('');
            setIsSubmitting(false);
        }
    }, [visible]);

    const noStatuses = statuses.length === 0;

    // Memoised so the controlled `values` prop keeps a stable reference across
    // re-renders. Without this, opening the dropdown re-renders with a fresh
    // array and react-dropdown-select resets/closes the just-opened menu (the
    // "broken on first click" bug).
    const selectedValues = useMemo(
        () => statuses.filter((opt) => opt.value === statusId),
        [statuses, statusId],
    );

    const handleConfirm = async () => {
        if (isSubmitting || noStatuses) return;
        if (!statusId) {
            setStatusError('Please choose a status to reopen the incident into.');
            return;
        }
        setStatusError('');

        setIsSubmitting(true);
        try {
            const success = await Promise.resolve(onConfirm(statusId));
            if (success !== false) onClose();
        } catch {
            // Confirm threw — modal stays open so the user can retry
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={() => { if (!isSubmitting) onClose(); }}
            footer={null}
            width={560}
            className="reopen-incident-modal"
            destroyOnClose
            centered>
            <div className="reopen-incident-container">
                <h2 className="heading">Reopen Incident</h2>
                <p className="description">
                    Reopening clears the completion details (including the resolution) and
                    returns the incident to the status you choose below.
                </p>

                <div className="status-field">
                    <h3>Status</h3>
                    {noStatuses ? (
                        <span className="field-error">No statuses are available to reopen this incident into.</span>
                    ) : (
                        <>
                            <div className="dropdown-wrapper">
                                <Dropdown
                                    options={statuses}
                                    onSelect={(values) => { setStatusId(values[0]?.value || ''); setStatusError(''); }}
                                    values={selectedValues}
                                    placeholder="Select Status" />
                            </div>
                            {statusError && <span className="field-error">{statusError}</span>}
                        </>
                    )}
                </div>

                <div className="button-wrapper">
                    <button type="button" className="confirm-button" onClick={handleConfirm} disabled={isSubmitting || noStatuses}>
                        {isSubmitting ? 'Reopening...' : 'Reopen Incident'}
                    </button>
                    <button type="button" className="cancel-button" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ReopenIncidentModal;
